import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { findingsApi, type CopilotFindingDto } from '../../api/findings';
import { sendCopilotMessage } from '../../components/CopilotPanel';

/**
 * Mano užklausos — the user's OWN tickets with status (owner ask 2026-09-10:
 * 'clients see their findings and status'). The workspace-scoped list of
 * radiniai they (or the copilot in their chats) registered, each with its
 * live status: Užregistruota → Eilėje → Taisoma → Pataisyta. No admin
 * chrome — no priorities, no exports, no worker plane; just "my reports and
 * where they stand".
 */

/** Status chip: sitting → queued → being fixed → done (+ rejected). */
const STATUS_CHIP: Record<string, string> = {
  Registered: 'bg-gray-100 text-gray-600',
  Queued: 'bg-blue-50 text-blue-700',
  PickedUp: 'bg-amber-100 text-amber-700',
  InProgress: 'bg-amber-100 text-amber-700',
  Fixed: 'bg-green-100 text-green-700',
  Done: 'bg-green-100 text-green-700',
  Rejected: 'bg-gray-100 text-gray-400',
};

const CATEGORY_CHIP: Record<string, string> = {
  ClientPattern: 'bg-amber-100 text-amber-700',
  ProductBug: 'bg-red-100 text-red-700',
  Enhancement: 'bg-primary-50 text-primary-700',
};

/** Offset-less MySQL DATETIME = UTC (house contract; see FindingsBoard). */
function parseAsUtc(iso: string): Date {
  return new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
}

function ageLabel(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const ms = Date.now() - parseAsUtc(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return t('findings.ageDays', { days });
  return t('findings.ageHours', { hours: Math.max(0, Math.floor(ms / 3_600_000)) });
}

export default function MyFindings() {
  const { t } = useTranslation();
  const { data: findings, isLoading } = useQuery({
    queryKey: ['my-findings'],
    queryFn: () => findingsApi.list(),
    refetchInterval: 60_000,
  });

  const statusLabel = (s: string) =>
    ['Registered', 'Queued', 'PickedUp', 'InProgress', 'Fixed', 'Done', 'Rejected'].includes(s)
      ? t(`findings.status.${s}`)
      : s;
  const catLabel = (c: string) =>
    ['ClientPattern', 'ProductBug', 'Enhancement'].includes(c) ? t(`findings.cat.${c}`) : c;

  const byAge = [...(findings ?? [])].sort(
    (a, b) => parseAsUtc(b.createdAtUtc).getTime() - parseAsUtc(a.createdAtUtc).getTime(),
  );

  // NEW REQUEST: straight to the assistant, framed to listen (the copilot's §5
  // rules make it register what follows as product-bug/enhancement). One click
  // opens the chat pre-loaded — the user just describes what's wrong or wanted.
  const newRequest = () =>
    sendCopilotMessage(t('myFindings.newRequestPrompt'));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('myFindings.title')}</h1>
        <button
          onClick={newRequest}
          className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          + {t('myFindings.newRequest')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-4">{t('common.loading')}</p>
      ) : byAge.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-gray-400">{t('myFindings.empty')}</p>
          <button
            onClick={newRequest}
            className="mt-4 px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400"
          >
            {t('myFindings.newRequest')} →
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2 font-semibold">{t('findings.col.title')}</th>
                <th className="px-4 py-2 font-semibold w-36">{t('findings.col.category')}</th>
                <th className="px-4 py-2 font-semibold w-32">{t('findings.col.status')}</th>
                <th className="px-4 py-2 font-semibold w-24">{t('findings.col.age')}</th>
              </tr>
            </thead>
            <tbody>
              {byAge.map((f: CopilotFindingDto) => (
                <tr key={f.id} className="border-t border-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="text-gray-800">{f.title}</div>
                    {f.suggestedFix && (
                      <div className="text-xs text-gray-400 truncate max-w-xl">{f.suggestedFix}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${CATEGORY_CHIP[f.category] ?? 'bg-gray-100 text-gray-600'}`}>
                      {catLabel(f.category)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_CHIP[f.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {statusLabel(f.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">
                    {ageLabel(f.createdAtUtc, t)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
